'use client'

import { useEffect, useState, useRef, type FormEvent } from 'react'

const FORM_ENDPOINT = 'https://api.nevision.app/f/nPYGUdtA5PFJ'

// Must match "Honeypot field name" in the form's Nevision settings. A name
// bots want to fill beats the well-known `_gotcha`, which smarter bots skip.
const HONEYPOT_FIELD = 'website'

// Public site key of the Cloudflare Turnstile widget for ne-room.com. While
// unset the widget is not rendered, so this can ship before the keys exist.
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: { sitekey: string }) => string
      reset: (widgetId?: string) => void
    }
  }
}

export default function Contact() {
  // The gap between render and submit is one of Nevision's spam signals.
  const renderedAt = useRef(Date.now())
  const [submitting, setSubmitting] = useState(false)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error' | 'info'
  } | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const turnstileRef = useRef<HTMLDivElement>(null)
  const turnstileId = useRef<string | null>(null)

  // Rendered explicitly rather than via the script's auto-scan, which only
  // runs once on load and would miss this section after a client-side
  // navigation. The widget adds `cf-turnstile-response` to the form.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return
    const render = () => {
      if (window.turnstile && turnstileRef.current && turnstileId.current === null) {
        turnstileId.current = window.turnstile.render(turnstileRef.current, { sitekey: TURNSTILE_SITE_KEY })
      }
    }
    if (window.turnstile) return render()
    let script = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT}"]`)
    if (!script) {
      script = document.createElement('script')
      script.src = TURNSTILE_SCRIPT
      script.async = true
      document.head.appendChild(script)
    }
    script.addEventListener('load', render)
    return () => script?.removeEventListener('load', render)
  }, [])

  // Turnstile tokens are single-use, so every attempt needs a fresh one.
  const resetTurnstile = () => {
    if (turnstileId.current !== null) window.turnstile?.reset(turnstileId.current)
  }

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 5000)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const data = new FormData(event.currentTarget)
    data.set('_ts', String(renderedAt.current))

    // Without a token Nevision files the submission as spam, so don't send a
    // real lead before the widget has finished its check.
    if (TURNSTILE_SITE_KEY && !data.get('cf-turnstile-response')) {
      showNotification('Please wait a moment while we verify you are human, then try again.', 'info')
      return
    }

    setSubmitting(true)

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        // Without this Nevision answers with a 302 to its hosted thank-you page instead of JSON.
        headers: { Accept: 'application/json' },
        body: data,
      })

      if (res.ok) {
        showNotification("Message sent successfully! We'll get back to you soon.", 'success')
        formRef.current?.reset()
        renderedAt.current = Date.now()
      } else {
        showNotification('There was an error submitting the form. Please try again.', 'error')
      }
    } catch {
      showNotification('There was an error submitting the form. Please try again.', 'error')
    } finally {
      resetTurnstile()
      setSubmitting(false)
    }
  }

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <h2 className="contact-section-title font-logo">Claim Your Free Strategy Session</h2>
        <p className="contact-section-description">
          In 30 minutes, we will identify your biggest automation opportunities and show you exactly how custom software can solve them—no obligation, no pressure. We respond within 24 hours and only take on 3-4 new projects per month to ensure quality.
        </p>
        <form ref={formRef} className="contact-form" action={FORM_ENDPOINT} method="POST" onSubmit={handleSubmit}>
          {/* Spam trap: moved off-screen rather than display:none (which bots
              skip), left empty by people; anything in it files the submission as spam */}
          <div style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
            <label>
              Website
              <input type="text" name={HONEYPOT_FIELD} tabIndex={-1} autoComplete="off" />
            </label>
          </div>
          <div className="form-row">
            <div className="form-group form-group-half">
              <select id="iAmA" name="iAmA" aria-label="I am a..." className="form-input">
                <option value="">I am a...</option>
                <option value="business-owner">Business owner</option>
                <option value="non-technical-manager">Non-technical manager</option>
                <option value="technical-manager">Technical manager</option>
                <option value="project-manager">Project Manager</option>
                <option value="sales-marketing">Sales & Marketing</option>
                <option value="designer">Designer</option>
                <option value="developer">Developer</option>
                <option value="qa">QA</option>
                <option value="data-analytics">Data & Analytics</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group form-group-half">
              <input type="text" id="position" name="position" placeholder="Position" className="form-input" />
            </div>
          </div>
          <div className="form-group">
            <input type="email" id="email" name="email" placeholder="Email address" className="form-input" required />
          </div>
          <div className="form-row">
            <div className="form-group form-group-half">
              <input type="text" id="firstName" name="firstName" placeholder="First name" className="form-input" required />
            </div>
            <div className="form-group form-group-half">
              <input type="text" id="lastName" name="lastName" placeholder="Last name" className="form-input" required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-half">
              <select id="budgetRange" name="budgetRange" aria-label="My budget range" className="form-input">
                <option value="">My Budget Range</option>
                <option value="1k-50k">$1k - $50k</option>
                <option value="50k-150k">$50k - $150k</option>
                <option value="150k-500k">$150k - $500k</option>
                <option value="500k-1m">$500k - $1M</option>
                <option value="1m+">$1M+</option>
              </select>
            </div>
            <div className="form-group form-group-half">
              <input type="text" id="howDidYouHear" name="howDidYouHear" placeholder="How did you hear about us?" className="form-input" />
            </div>
          </div>
          <div className="form-group">
            <textarea id="projectDescription" name="projectDescription" placeholder="Project description" rows={6} className="form-input" required></textarea>
          </div>
          <div className="form-checkboxes">
            <label className="checkbox-label">
              <input type="checkbox" name="terms" required />
              <span>I agree to the terms of use</span>
            </label>
            <label className="checkbox-label">
              <input type="checkbox" name="privacy" required />
              <span>I agree to the privacy policy</span>
            </label>
          </div>
          {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="form-group" />}
          <button type="submit" className="contact-submit-btn font-logo" disabled={submitting}>
            {submitting ? 'Sending...' : 'Get My Free Consultation'}
          </button>
        </form>
        {notification && (
          <>
            {/* Backdrop */}
            <div className="newsletter-modal-backdrop" onClick={() => setNotification(null)} />
            {/* Modal */}
            <div className={`newsletter-modal ${notification.type}`}>
              <div className="newsletter-modal-content">
                <div className="newsletter-modal-message">{notification.message}</div>
                <button onClick={() => setNotification(null)} className="newsletter-modal-close" aria-label="Close notification">
                  ×
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  )
}
